import { useCallback, useEffect, useMemo, useState } from "react";
import type { RajasthanCollective } from "../../../models/rajasthan";
import { getGridcoCollectiveData } from "../../../Rest_api/RestApi";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { R_CHART_TYPES } from "../const";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { BrushStart, ChartType } from "../../../models/chart_model";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { FUNCTIONS } from "../const_gridco";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { stringToConsistentColor } from "../../../extensions/string";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
const GROUP_BY = [
  {
    label: "Product",
    key: "product__name",
  },
  {
    label: "Exchange",
    key: "exchange__name",
  },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];
const Y_AXIS = ["Price", "Cost"];

export default function Collective() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [data, setData] = useState<RajasthanCollective[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([
    GROUP_BY[0],
    GROUP_BY[1],
  ]);
  const [selectedYaxis, setSelectedYaxis] = useState(Y_AXIS[0]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);

  const [selectedFunction, setSelectedFunction] = useState(FUNCTIONS[1]);

  const getKeys = useCallback(() => {
    if (selectedKey.key === "time") {
      return ["date", "time_block"];
    } else if (selectedKey.key === "date") {
      return ["date"];
    } else {
      return [selectedKey.values[0]];
    }
  }, [selectedKey]);

  useEffect(() => {
    let filteredData = data;
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.time_block)
      );
    }

    const params: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "time_block"],
      groupBy: (selectedFunction.comparative
        ? selectedGroupKeys.map((item) => item.key)
        : []
      ).concat(["type"]),
      volumeKey: "value_mw",
      aggregationKeys: ["value_mwh", "cost"],
      priceKey: "wt_mcp_rs_kwh",

      data: filteredData,
    };

    const chartData = FormatChartData(params);

    const finalData = chartData.data.map((item) => {
      // Extract date and time_block from the current chart data item
      const currentDate = item.date;
      const currentTimeBlock = item.time_block;

      // Filter dataItems to only include ones matching the current date and time block
      const relevantDataItems = data.filter((dataItem) => {
        if (selectedKey.key === "time") {
          // For time view, match both date and time block
          return (
            dataItem.date === currentDate &&
            dataItem.time_block === currentTimeBlock
          );
        } else if (selectedKey.key === "date") {
          // For date view, match only the date
          return dataItem.date === currentDate;
        } else if (selectedKey.key === "month") {
          // For month view, match the month
          return dataItem.month === item.month;
        }
        return false;
      });

      // Now find the specific exchange products within the relevant time period
      const iex_dam = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM"
      );
      const iex_tam = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "TAM"
      );

      const iex_rtm = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM"
      );

      const iex_gdam = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "GDAM"
      );

      const pxil_rtm = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "PXIL" && dataItem.product__name === "RTM"
      );

      // Add type safety for all properties and handle NaN values
      const getValue = (obj: any, key: string) => {
        if (!obj) return 0;
        const value = obj[key];
        return typeof value === "number" && !isNaN(value) ? value : 0;
      };

      return {
        ...item,
        IEX_DAM_value_mw: getValue(iex_dam, "wt_mcp_rs_kwh"),
        IEX_RTM_value_mw: getValue(iex_rtm, "wt_mcp_rs_kwh"),
        IEX_GDAM_value_mw: getValue(iex_gdam, "wt_mcp_rs_kwh"),
        PXIL_RTM_value_mw: getValue(pxil_rtm, "wt_mcp_rs_kwh"),
        IEX_TAM_value_mw: getValue(iex_tam, "wt_mcp_rs_kwh"),
      };
    });

    const finalTableData = chartData.tableData?.map((item) => {
      // Extract date and time_block from the current chart data item
      const currentDate = item.date;
      const currentTimeBlock = item.time_block;

      // Filter dataItems to only include ones matching the current date and time block
      const relevantDataItems = data.filter((dataItem) => {
        if (selectedKey.key === "time") {
          // For time view, match both date and time block
          return (
            dataItem.date === currentDate &&
            dataItem.time_block === currentTimeBlock
          );
        } else if (selectedKey.key === "date") {
          // For date view, match only the date
          return dataItem.date === currentDate;
        } else if (selectedKey.key === "month") {
          // For month view, match the month
          return dataItem.month === item.month;
        }
        return false;
      });

      // Now find the specific exchange products within the relevant time period
      const iex_dam = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM"
      );
      const iex_tam = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "TAM"
      );

      const iex_rtm = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM"
      );

      const iex_gdam = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "GDAM"
      );

      const pxil_rtm = relevantDataItems.find(
        (dataItem) =>
          dataItem.exchange__name === "PXIL" && dataItem.product__name === "RTM"
      );

      // Add type safety for all properties and handle NaN values
      const getValue = (obj: any, key: string) => {
        if (!obj) return 0;
        const value = obj[key];
        return typeof value === "number" && !isNaN(value) ? value : 0;
      };

      return {
        ...item,
        IEX_DAM_value_mw: getValue(iex_dam, "wt_mcp_rs_kwh"),
        IEX_RTM_value_mw: getValue(iex_rtm, "wt_mcp_rs_kwh"),
        IEX_GDAM_value_mw: getValue(iex_gdam, "wt_mcp_rs_kwh"),
        PXIL_RTM_value_mw: getValue(pxil_rtm, "wt_mcp_rs_kwh"),
        IEX_TAM_value_mw: getValue(iex_tam, "wt_mcp_rs_kwh"),
      };
    });

    setTableData(finalTableData || []);
    setChartData(finalData);
    setChartKeys(chartData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedFunction,
    getKeys,
    selectedBlock,
    selectedKey.key,
  ]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const response = await getGridcoCollectiveData({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    const processedCollectiveData = response.map((item) => {
      const isBuy = item.value_mw > 0;
      return {
        ...item,
        type: isBuy ? "Buy" : "Sell",
        time_block: item.time_slot,
        entity: item.exchange__name + " " + item.product__name,
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        cost: Math.abs(item.cost),
        month: getMonthFromDate(item.date),
      };
    });

    setUniqueBlock(
      Array.from(
        new Set(processedCollectiveData.map((item) => item.time_block))
      )
    );
    setData(processedCollectiveData);
    setLoading(false);
  }, [activeTab, DefaultDates]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedKey.key === "time") {
      if (selectedBlock.length > 0) {
        return "time_block";
      }
      return "time_block";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedKey.key;
    }
  }, [selectedBlock.length, selectedKey.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedKey.isTime
    ? [
        "time_block", // Changed dateIndex to date
        selectedKey.values[1],
      ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  const legends = useMemo(() => {
    const volumeLegends = chartKeys.map((key) => ({
      dataKey: key + "_value_mw",
      name: key.replaceAll("_", " "),
      type: selectedChartType.value,
      stackId: selectedChartType.stackId
        ? key.includes("Buy")
          ? "Buy"
          : "Sell"
        : undefined,
    }));

    const secondaryAxisLegends =
      selectedYaxis === "Price"
        ? ["IEX_DAM", "IEX_RTM", "IEX_GDAM", "PXIL_RTM", "IEX_TAM"].map((key) => ({
            dataKey: key + "_value_mw",
            name: key.replaceAll("_", " ") + " Price",
            stroke: stringToConsistentColor(key.replaceAll("_", " ") + " Price"),
            type: ChartType.Line,
            yAxisId: "right",
            stackId: undefined,
          }))
        : chartKeys.map((key) => ({
            dataKey: key + "_cost",
            name:
              key.replaceAll("_", " ") +
              (key.includes("Buy") ? " Cost" : " Revenue"),
            stroke: stringToConsistentColor(
              key.replaceAll("_", " ") +
                (key.includes("Buy") ? " Cost" : " Revenue")
            ),
            type: ChartType.Line,
            yAxisId: "right",
            stackId: selectedChartType.stackId
              ? key.includes("Buy")
                ? "Cost"
                : "Revenue"
              : undefined,
          }));

    return [...volumeLegends, ...secondaryAxisLegends];
  }, [chartKeys, selectedChartType, selectedYaxis]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData || [],
    tableData: TableData,
    itemsPerPage: 96,
    unit: "MW",
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Power Procurement By Collective",
  };

  function secondDataKey() {
    if (selectedKey.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }


  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            items={KEYS.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
            title={"Interval"}
          />
          <FilterMenu
            title="Function"
            items={FUNCTIONS.map((item) => item.label)}
            selectedItems={[selectedFunction.label]}
            onSelect={(item, index) => {
              setSelectedFunction(FUNCTIONS[index]);
            }}
          />
          <FilterMenu
            title="Y Axis"
            items={Y_AXIS}
            selectedItems={[selectedYaxis]}
            onSelect={(item, index) => {
              setSelectedYaxis(Y_AXIS[index]);
            }}
          />

          {selectedFunction.comparative && (
            <FilterMenu
              title="Group By"
              items={GROUP_BY.map((item) => item.label)}
              selectedItems={selectedGroupKeys.map((item) => item.label)}
              setSelectedItems={(items) => {
                setSelectedGroupKeys(
                  items.map((item) => {
                    return GROUP_BY.find((group) => group.label === item)!;
                  })
                );
              }}
            />
          )}
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Collective Volume" subtitle="Fetching Gridco collective power procurement data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Collective Volume (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  const secureTablePar = {
                    ...tablePar,
                    data: chartData || [],
                  };
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(secureTablePar);
                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(secureTablePar);
                  dataTab.label === "Table" &&
                    downloadChartTableCSV(secureTablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              unit="MW"
              secondYAxisLabel="Price (Rs/kWh)"
              xDataKey={xDataKey}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              xTick={
                selectedKey.isTime && selectedBlock.length == 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={secondDataKey()}
              isTimeSlot={selectedKey.isTime}
              onlyTitle={true}
              legends={legends}
              defaultSelectedLegends={selectedLegends}
              onLegendSelect={handleLegendSelect}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div> Gridco : Exchange Sell Purchase</div>}
              text="1.	Comparative gives product and exchange segmentation. 
              "
            >
              <p className="gna-footer-text"> Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
